import React, { useCallback, useEffect, useMemo, useState, useContext } from 'react'
import { Bleed, Button, Row, Separator, Skeleton, useOverlay, useStatusOverlay } from '@fs/zion-ui'
import { useUser } from '@fs/zion-user'
import { useQueryParams } from '@fs/zion-router'
import { useLazyQuery, useMutation } from '@apollo/client/react'
import { OverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import { workflowStatuses } from '../../helpers'
import styles from '../../groupData/GroupData.styles'
import { useGroupData, useImageOrganizeActions, useImageOrganizeModes } from '../../hooks'
import groupDataTransl from '../../groupData/GroupData.transl'
import queries from '../../queries'
import mutations from '../../mutations'
import transl from './ImageOrganizeFooter.transl'
import { NoDivisionsDialog, DivideStatusDialog, CorrectMetadataFinishedDialog } from './Dialogs/Dialogs'
import { ButtonWithToolTip, ItemInProgressByAnotherUser, StatusAlreadySetView } from './Components'
import fsOperationsCallHeader from '../../../../utils/fsOperationsCallHeader'
import { useSetFocusedIndex } from '../../../../components/groupImageBrowser/hooks'

const ImageOrganizeFooter = () => {
  const { isDivideMode, isMetadataAllMode, isMetadataSingleMode } = useImageOrganizeModes()

  return (
    <>
      <Separator size="xxl" />
      <Bleed sides>
        <div css={styles.infoSheetFooter}>
          {isDivideMode && <DivideActions />}
          {(isMetadataAllMode || isMetadataSingleMode) && (
            <MetadataActions isMetadataSingleMode={isMetadataSingleMode} />
          )}
        </div>
      </Bleed>
    </>
  )
}

function DivideActions() {
  const overlay = useOverlay()
  const noDivisionsOverlay = useOverlay()
  const { dividerOffsets, dividersAltered } = useImageOrganizeActions()
  const { initialGroup: parentGroup, group } = useGroupData()
  const [doUpdateNaturalGroups, { data: updateData, error: updateError }] = useMutation(mutations.updateNaturalGroups())
  const [doUpdateWorkflowActionStatus] = useMutation(mutations.updateWorkflowActionStatus())
  const [fetchStatus, { data: statusData, error: statusError, stopPolling: stopPollingStatus }] = useLazyQuery(
    queries.naturalGroupAsyncStatus(),
    {
      pollInterval: 1000,
    }
  )

  const [isDivideComplete, setIsDivideComplete] = useState(false)
  const [numItems, setNumItems] = useState(-1)
  const [noDivisionsError, setNoDivisionsError] = useState(false)

  const hasDivisions = dividerOffsets?.length > 1

  const doSaveNewDividers = useCallback(() => {
    async function workflowUpdate() {
      const resp = await doUpdateWorkflowActionStatus({
        variables: { params: { action: 'AuditBoundaries', newStatus: 'Completed', rmsId: group.id } },
        context: { headers: { ...fsOperationsCallHeader } },
      })
      if (parseInt(resp.data.updateWorkflowActionStatus.code, 10) > 299) setNoDivisionsError(true)

      setIsDivideComplete(true)
    }

    const newNaturalGroups = getNewNaturalGroups(dividerOffsets, parentGroup?.imageApids)

    if (!hasDivisions) workflowUpdate()
    if (dividersAltered) {
      doUpdateNaturalGroups({
        variables: { parentGroupId: parentGroup?.id, newNaturalGroups },
        context: { headers: { ...fsOperationsCallHeader } },
      })
    } else setIsDivideComplete(true)

    setNumItems(newNaturalGroups?.length || -1)
    overlay.handleClick()
  }, [
    dividerOffsets,
    parentGroup,
    hasDivisions,
    doUpdateWorkflowActionStatus,
    group,
    overlay,
    doUpdateNaturalGroups,
    dividersAltered,
  ])

  const handleClick = useCallback(() => {
    if (!hasDivisions) noDivisionsOverlay.open()
    else doSaveNewDividers()
  }, [doSaveNewDividers, hasDivisions, noDivisionsOverlay])

  useEffect(() => {
    const rmsStatusLocation = updateData?.updateNaturalGroups
    if (statusError) stopPollingStatus()

    if (statusData?.naturalGroupAsyncStatus?.percentComplete === 1) {
      setIsDivideComplete(true)
      stopPollingStatus()
    }
    if (rmsStatusLocation && !isDivideComplete) fetchStatus({ variables: { location: rmsStatusLocation } })
  }, [statusError, stopPollingStatus, statusData, fetchStatus, isDivideComplete, updateData])

  return (
    <>
      <Row guttersY="none" alignX="end">
        <Button emphasis="high" disabled={!parentGroup?.imageApids} onClick={handleClick}>
          {groupDataTransl.doneDividing()}
        </Button>
      </Row>
      <DivideStatusDialog
        overlay={overlay}
        isDivideComplete={isDivideComplete}
        statusError={statusError}
        updateError={updateError || noDivisionsError}
        numItems={numItems}
        percentComplete={((statusData?.naturalGroupAsyncStatus?.percentComplete ?? 0) * 100).toFixed(0)}
        parentGroup={parentGroup}
      />
      {!hasDivisions && <NoDivisionsDialog overlay={noDivisionsOverlay} doSaveNewDividers={doSaveNewDividers} />}
    </>
  )
}

function getNewNaturalGroups(dividerOffsets, imageApids) {
  if (Array.isArray(dividerOffsets) && Array.isArray(imageApids)) {
    const newNaturalGroups = []

    for (let i = 0; i < dividerOffsets.length; i++) {
      const start = dividerOffsets[i]
      const end = dividerOffsets[i + 1] || imageApids.length
      const naturalGroupIds = imageApids.slice(start, end)
      newNaturalGroups.push({ ids: naturalGroupIds })
    }

    return newNaturalGroups
  }

  return null
}

function MetadataActions({ isMetadataSingleMode }) {
  const { query, setQuery } = useQueryParams()
  const { cisId } = useUser()
  const { finishedCorrectingMetadataOverlay } = useContext(OverlayContext)
  const {
    initialGroup,
    initialChildId,
    setGroupIndex,
    groupIndex,
    loading: loadingGroup,
    itemNumber,
    itemCount,
  } = useGroupData()
  const {
    updateMetadataWorkflowStatus,
    loading: updateWorkflowLoading,
    setSkippedStatus,
    getSkippedStatus,
  } = useImageOrganizeActions()
  const [fetchImageOrganizeStatus, { data: statusData, loading: statusLoading }] = useLazyQuery(
    queries.imageOrganizeStatus()
  )

  const setFocusedIndex = useSetFocusedIndex()

  const showStatusOverlay = useStatusOverlay()
  const [lastGroupId, setLastGroupId] = useState(null)

  const currentGroup = useMemo(
    () => initialGroup?.childGroups?.[groupIndex] ?? initialGroup,
    [initialGroup, groupIndex]
  )

  const fetchNewImageOrganizeStatus = useCallback(async () => {
    if (statusLoading) return
    await fetchImageOrganizeStatus({
      variables: { rmsId: currentGroup.id },
      context: { headers: { ...fsOperationsCallHeader } },
    })
    setLastGroupId(currentGroup.id)
  }, [statusLoading, fetchImageOrganizeStatus, currentGroup])

  useEffect(() => {
    if (currentGroup?.id && currentGroup.id !== lastGroupId) fetchNewImageOrganizeStatus()
  }, [currentGroup, lastGroupId, fetchNewImageOrganizeStatus])

  const isFinalItem =
    isMetadataSingleMode || groupIndex === (initialGroup?.childGroups?.length || 0) - 1 || itemCount === itemNumber

  const moveToNextItem = useCallback(() => {
    if (!isFinalItem) {
      const newGroupIndex = groupIndex + 1
      setGroupIndex(newGroupIndex)
      const newId = initialGroup?.childGroups[itemNumber].id
      if (newId) setQuery({ ...query, groupId: newId })
      // set focused index to first image in new group
      setFocusedIndex(0, null, newGroupIndex)
    } else finishedCorrectingMetadataOverlay.open()
  }, [
    initialGroup,
    setGroupIndex,
    finishedCorrectingMetadataOverlay,
    query,
    setQuery,
    isFinalItem,
    groupIndex,
    itemNumber,
    setFocusedIndex,
  ])

  const markCurrentItem = useCallback(
    async (newStatus, errorMessage = '', moveToNext = true) => {
      const success = await updateMetadataWorkflowStatus(currentGroup?.id, newStatus)
      if (success && moveToNext) moveToNextItem()
      else if (!success && errorMessage) showStatusOverlay({ message: errorMessage, dismissable: true, type: 'error' })
    },
    [moveToNextItem, showStatusOverlay, updateMetadataWorkflowStatus, currentGroup]
  )

  const markItemAsComplete = useCallback(
    () => markCurrentItem(workflowStatuses.COMPLETED, transl.errorMarkingItemMessage()),
    [markCurrentItem]
  )

  const doResetStatus = useCallback(async () => {
    await markCurrentItem(workflowStatuses.IN_PROGRESS, '', false)
    setSkippedStatus(currentGroup.id, false)
    fetchImageOrganizeStatus({
      variables: { rmsId: currentGroup?.id },
      context: { headers: { ...fsOperationsCallHeader } },
    })
  }, [currentGroup, fetchImageOrganizeStatus, markCurrentItem, setSkippedStatus])

  const doSkipItem = useCallback(() => {
    setSkippedStatus(currentGroup.id, true)
    moveToNextItem()
  }, [setSkippedStatus, currentGroup, moveToNextItem])

  const disableButtons =
    updateWorkflowLoading || loadingGroup || (isMetadataSingleMode && currentGroup?.id !== initialChildId)

  const currentGroupStatus = useMemo(() => statusData?.imageOrganizeStatus?.correctMetadata?.status, [statusData])
  const reservedByAnother = useMemo(
    () =>
      currentGroupStatus === workflowStatuses.IN_PROGRESS &&
      statusData?.imageOrganizeStatus?.correctMetadata?.userId !== cisId,
    [cisId, currentGroupStatus, statusData]
  )
  const isStatusAlreadySet =
    (!reservedByAnother &&
      Boolean(currentGroupStatus) &&
      currentGroupStatus !== workflowStatuses.IN_PROGRESS &&
      currentGroupStatus !== workflowStatuses.AVAILABLE) ||
    getSkippedStatus(currentGroup.id)

  const ButtonRow = useCallback(() => {
    const display = !isStatusAlreadySet && !reservedByAnother

    if (display) {
      if (disableButtons)
        return (
          <Row guttersY="none" alignX="end">
            <ButtonWithToolTip emphasis="low" disabled onClick={doSkipItem} buttonText={groupDataTransl.skip()} />
            <ButtonWithToolTip disabled onClick={markItemAsComplete} buttonText={groupDataTransl.metadataIsCorrect()} />
          </Row>
        )

      return (
        <Row guttersY="none" alignX="end">
          <ButtonWithToolTip
            onClick={doSkipItem}
            emphasis="low"
            buttonText={groupDataTransl.skip()}
            label={transl.skipMessageHeader()}
            description={transl.skipMessageBody()}
          />
          <Button emphasis="high" onClick={markItemAsComplete}>
            {groupDataTransl.metadataIsCorrect()}
          </Button>
        </Row>
      )
    }
    return null
  }, [disableButtons, doSkipItem, reservedByAnother, isStatusAlreadySet, markItemAsComplete])
  const showLoading = statusLoading || !currentGroup

  return showLoading ? (
    <Skeleton.Button minWidth={330} />
  ) : (
    <>
      {isStatusAlreadySet && (
        <StatusAlreadySetView
          disabled={disableButtons}
          onResetClick={doResetStatus}
          onNextItemClick={moveToNextItem}
          currentGroupStatus={currentGroupStatus}
        />
      )}
      {reservedByAnother && <ItemInProgressByAnotherUser disabled={disableButtons} onNextItemClick={moveToNextItem} />}
      <ButtonRow />
      <CorrectMetadataFinishedDialog overlay={finishedCorrectingMetadataOverlay} />
    </>
  )
}

export default ImageOrganizeFooter
